import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "../styles/RoomDetail.css";
import { useNavbar } from "../hooks/useNavbar";
import { useLanguage } from "../context/LanguageContext";
import { useBookingCart, MAX_GROUP_ROOMS } from "../context/BookingCartContext";
import Toast from "../components/Toast";
import { t } from "../i18n";
import { formatBeds } from "../utils/beds";
import API from "../config";

function RoomDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // ── เลือก Navbar ตาม role ──────────────────────────────────────
  const NavbarComponent = useNavbar();
  const { lang } = useLanguage();

  const [room, setRoom]         = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  /* โชว์ spinner เฉพาะเมื่อโหลดนานเกิน 300ms — โหลดไวจะไม่เห็นกระพริบ */
  useEffect(() => {
    const t = setTimeout(() => setShowSpinner(true), 300);
    return () => clearTimeout(t);
  }, []);

  /* วันที่/จำนวนคนอ่านจากตะกร้าก่อน (รอดตอนกด Back) แล้วค่อย fallback ไป location.state
     เผื่อ deep link หรือ history entry เก่าที่ไม่มี context */
  const cart = useBookingCart();
  const state      = location.state || {};
  const checkIn    = cart?.checkIn  || state.checkIn || "";
  const nights     = cart?.nights   || state.nights  || 1;
  const adults     = cart?.adults   || state.adults  || 1;

  /* ห้องว่างของประเภทนี้ — หน้า Rooms ส่งมาให้ ไม่ต้องยิง availability ซ้ำ
     undefined = ไม่รู้ (deep link) ปล่อยให้ไปตายตอน checkout ซึ่ง server เช็คจริงอยู่แล้ว */
  const free = Number.isFinite(state.free) ? state.free : undefined;

  const [toasting, setToasting] = useState(null);
  const toast = (msg, type = "error") => {
    setToasting({ msg, type });
    setTimeout(() => setToasting(null), 3000);
  };

  const checkOut = (() => {
    if (!checkIn) return "";
    const d = new Date(checkIn);
    d.setDate(d.getDate() + nights);
    return d.toISOString().split("T")[0];
  })();

  const totalPrice = nights * (room?.price || 0);

  useEffect(() => {
    setLoadError(false);
    fetch(`${API}/api/rooms/${id}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        if (!d || !d._id) throw new Error("invalid room data");
        setRoom(d);
        setActiveIdx(0);
      })
      .catch(err => {
        console.error("[RoomDetail] load failed:", err);
        setLoadError(true);
      });
  }, [id]);

  const fmtDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric"
    });
  };

  /* จองห้องเดียวทันที — ยังส่ง state ทรงเดิมที่ Payment + backend รองรับอยู่ ไม่ผ่านตะกร้า */
  const handleBooking = () => {
    navigate("/payment", {
      state: { room, checkIn, checkOut, nights, totalPrice, persons: adults, roomsCount: 1 }
    });
  };

  const inCart     = cart?.qtyOf(id) || 0;
  const cartCap    = Math.min(
    Number.isFinite(free) ? free : MAX_GROUP_ROOMS,
    MAX_GROUP_ROOMS - (cart?.totalRooms || 0) + inCart,
  );
  const cartLocked = !checkIn || free === 0 || inCart >= cartCap;

  const handleAddToCart = () => {
    if (cartLocked) return;
    cart.addOne(id, free);
    toast(t(lang, "cartAdded", lang === "en" && room?.nameEn ? room.nameEn : room?.name || ""), "success");
  };

  if (loadError) return (
    <div className="rd-loading" style={{ flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: 40 }}>🏨</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>{t(lang, "rdLoadFail")}</div>
      <div style={{ fontSize: 13, color: "#9ca3af" }}>{t(lang, "rdServerError")}</div>
      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        <button onClick={() => window.location.reload()}
          style={{ padding: "10px 22px", borderRadius: 999, border: "1px solid #d1d5db", background: "#fff",
                   fontSize: 13.5, fontWeight: 600, color: "#374151", cursor: "pointer", fontFamily: "inherit" }}>
          {t(lang, "retry")}
        </button>
        <button onClick={() => navigate("/rooms")}
          style={{ padding: "10px 22px", borderRadius: 999, border: "none", background: "#111",
                   fontSize: 13.5, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
          {t(lang, "backToRooms")}
        </button>
      </div>
    </div>
  );

  if (!room) return (
    <div className="rd-loading">{showSpinner && <div className="rd-spinner" />}</div>
  );

  const imgUrl = (img) => `/uploads/${img}`;

  // รวมรูปหลัก + รูปเพิ่มเติม สำหรับแกลเลอรี (ตัดค่าว่าง/ซ้ำออก)
  const gallery = [room.image, ...(room.images || [])].filter((v, i, a) => v && a.indexOf(v) === i);
  const hasMany = gallery.length > 1;
  const curIdx  = Math.min(activeIdx, gallery.length - 1);
  const altText = lang === "en" && room.nameEn ? room.nameEn : room.name;

  // "" เมื่อห้องยังไม่ได้ระบุเตียง → ซ่อนบรรทัดเตียงไปเลย ดีกว่าเดาข้อมูลให้
  const bedText = formatBeds(lang, room.beds);

  const goPrev = () => setActiveIdx((curIdx - 1 + gallery.length) % gallery.length);
  const goNext = () => setActiveIdx((curIdx + 1) % gallery.length);

  return (
    <div className="rd-page">
      <NavbarComponent />

      <div className="rd-container">

        {/* ══ LEFT: Images ══ */}
        <div className="rd-images">
          <div className="rd-main-img">
            <img src={imgUrl(gallery[curIdx] || room.image)} alt={altText} />

            {/* ปุ่มเลื่อน ‹ › — โชว์เฉพาะเมื่อมีหลายรูป */}
            {hasMany && (
              <>
                <button className="rd-nav rd-nav-prev" onClick={goPrev} aria-label="previous image">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <button className="rd-nav rd-nav-next" onClick={goNext} aria-label="next image">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* แถว thumbnail — จำนวนเท่ารูปที่อัปจริง โชว์เฉพาะเมื่อมีมากกว่า 1 รูป */}
          {hasMany && (
            <div className="rd-thumbs">
              {gallery.map((img, i) => (
                <div
                  key={img}
                  className={`rd-thumb ${i === curIdx ? "active" : ""}`}
                  onClick={() => setActiveIdx(i)}
                >
                  <img src={imgUrl(img)} alt={altText} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ══ RIGHT: Info ══ */}
        <div className="rd-info">
          <h1 className="rd-title">{lang === "en" && room.nameEn ? room.nameEn : room.name}</h1>

          <div className="rd-cols">

            {/* Highlight */}
            <div>
              <h4 className="rd-col-title">{t(lang, "highlight")}</h4>
              <ul className="rd-list">
                {room.breakfast && (
                  <li><span className="rd-li-icon">🍳</span> {t(lang, "breakfastIncluded")}</li>
                )}
                {bedText && (
                  <li><span className="rd-li-icon">🛏</span> {bedText}</li>
                )}
                <li><span className="rd-li-icon">🐾</span> {t(lang, "noPetsAllowed")}</li>
              </ul>
            </div>

            {/* Facilities */}
            <div>
              <h4 className="rd-col-title">{t(lang, "facilities")}</h4>
              <div className="rd-facilities">
                {[
                  { icon: "🚿", key: "facWaterHeater" },
                  { icon: "📺", key: "facTelevision" },
                  { icon: "❄️", key: "facAirCon" },
                  { icon: "📶", key: "facWifi" },
                  { icon: "🫧", key: "facWasher" },
                  { icon: "🧊", key: "facFridge" },
                ].map(f => (
                  <div key={f.key} className="rd-facility">
                    <span>{f.icon}</span>
                    <span>{t(lang, f.key)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rd-divider" />

          {/* Booking form */}
          <div className="rd-form">

            <div className="rd-form-row">
              <div className="rd-field">
                <label>{t(lang, "rdCheckIn")}</label>
                <div className="rd-select-box">
                  <span>{fmtDate(checkIn)}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>

              <div className="rd-field">
                <label>{t(lang, "rdCheckOut")}</label>
                <div className="rd-select-box">
                  <span>{fmtDate(checkOut)}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>
            </div>

            <div className="rd-field rd-field-person">
              <label>{t(lang, "rdPerson")}</label>
              <div className="rd-select-box">
                <span>{adults}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>

            <div className="rd-bottom">
              <div className="rd-price-block">
                <span className="rd-price-main">{room.price?.toLocaleString()} THB</span>
                {/* จองหลายห้องแต่คืนเดียวก็ต้องโชว์ยอดรวม ไม่งั้นเห็นแค่ราคา/ห้อง แล้วโดนเก็บหลายเท่า */}
                {nights > 1 && (
                  <span className="rd-price-sub">
                    {nights} {t(lang, "nightsUnit2")}
                    {" = "}{totalPrice.toLocaleString()} THB
                  </span>
                )}
              </div>
              <div className="rd-btn-row">
                <button className="rd-cart-btn" onClick={handleAddToCart} disabled={cartLocked}>
                  {inCart > 0 ? `${t(lang, "rdAddToCart")} (${inCart})` : t(lang, "rdAddToCart")}
                </button>
                <button className="rd-book-btn" onClick={handleBooking}>
                  {t(lang, "rdBookBtn")}
                </button>
              </div>
            </div>

          </div>
        </div>

      </div>
      <Toast show={!!toasting} type={toasting?.type} message={toasting?.msg} lang={lang} />
    </div>
  );
}

export default RoomDetail;